import { INITIAL_LAYOUT_SINGLE_PAGE } from 'mytours-read-common'
import { FileRec, GetCollectionsShowResponse, GetScreensShowResponse, MediaItem } from 'mytours-read-api-typescript'

import { ImageSizePreset, ImageSizePresetMap } from '../../common_lib/types'
import { generateImageUrl } from '../../common_lib/utils'
import { normalizeImageKey } from '../../sw/imageKey'
import { resolveAudioSource } from '../audio/audioSource'
import { ContentLocator } from './locator'
import { ItemType, VersionedManifest } from './registry'

/**
 * The manifest sections a download walks. Themes are absent because the project publishes one theme at
 * its root rather than one per id. How each is addressed is the locator's business, not this module's:
 * a published project spells them as folders of hashids, a direct-API one as `/api/v3/<type>/<id>/…`.
 */
const DOWNLOADABLE_TYPES: ItemType[] = [
	'collections',
	'screens',
	'media_items',
	'quizzes',
	'maps',
	'forms',
	'badge_groups',
]

export interface CollectionItem {
	item_type?: string
	item_id?: number
	cover_image?: MediaItem
	cover_image_grid?: MediaItem
}

/** Item types a collection item's singular `item_type` can name. */
const ITEM_TYPE_SECTIONS: Record<string, ItemType> = {
	screen: 'screens',
	collection: 'collections',
	media_item: 'media_items',
	quiz: 'quizzes',
	map: 'maps',
	form: 'forms',
}

/**
 * One thing to download.
 *
 * `urls` are the candidates the read API would try in order — the requested language, then its similar
 * languages, then the untagged primary — so an item published only in a fallback language is still stored.
 * `cacheKey` is where the body goes when it differs from the URL that fetched it, which is how an image
 * fetched at one signed size answers a request for another.
 */
export interface DownloadEntry {
	type: ItemType | 'media_file' | 'collection_items' | 'collection_triggers' | 'collection_full'
	id: number
	urls: string[]
	cacheKey?: string
}

export interface ExpandContext {
	api: ContentLocator
	accountId: number
	language: string
	devicePixelRatio: number
}

interface IndexedItem {
	version: number
	accountId?: number
}

/** item type -> id -> what the manifest knows about it. */
export type ItemIndex = Partial<Record<ItemType, Map<number, IndexedItem>>>

export const indexManifest = (manifest: VersionedManifest): ItemIndex => {
	const index: ItemIndex = {}

	for (const type of DOWNLOADABLE_TYPES) {
		const items = manifest[type]
		if (!items?.length) continue

		const byId = new Map<number, IndexedItem>()
		for (const item of items) {
			const { id, version } = item
			if (id === undefined || version === undefined) continue
			byId.set(id, { version, accountId: (item as { account_id?: number }).account_id })
		}
		if (byId.size > 0) index[type] = byId
	}

	return index
}

const entryFor = (type: ItemType, id: number, index: ItemIndex, context: ExpandContext): DownloadEntry | undefined => {
	const item = index[type]?.get(id)
	if (!DOWNLOADABLE_TYPES.includes(type) || !item) return undefined

	const { api, accountId, language } = context

	return { type, id, urls: api.resourceUrls(item.accountId ?? accountId, type, id, item.version, language) }
}

/** A collection needs its own metadata, its ordered item list, and the triggers that fire on it. */
const collectionEntries = (id: number, index: ItemIndex, context: ExpandContext): DownloadEntry[] => {
	const item = index.collections?.get(id)
	if (!item) return []

	const { api, accountId, language } = context
	const owner = item.accountId ?? accountId
	const metadata = entryFor('collections', id, index, context)

	return [
		...(metadata ? [metadata] : []),
		{ type: 'collection_items', id, urls: api.collectionIndexUrls(owner, id, item.version, language, 'items') },
		{ type: 'collection_triggers', id, urls: api.collectionIndexUrls(owner, id, item.version, language, 'triggers') },
	]
}

/** The fields the walk reads off a section slot or a media group item. */
type MediaRef = Pick<MediaItem, 'id' | 'thumbnail_media_item'>

/**
 * The media items a screen references, in section order. A media group keeps them in `section_items` beside
 * link items, whose id is not a media item's, so only an item that names a media type counts.
 */
export const screenMediaItemIds = (screen: GetScreensShowResponse): number[] => {
	const ids: number[] = []

	const add = (mediaItem?: MediaRef) => {
		if (mediaItem?.id !== undefined && !ids.includes(mediaItem.id)) ids.push(mediaItem.id)
		if (mediaItem?.thumbnail_media_item) add(mediaItem.thumbnail_media_item)
	}

	add(screen.background_image)

	for (const section of screen.sections ?? []) {
		add(section.media_item)
		add(section.left_media_item)
		add(section.right_media_item)
		for (const mapItem of section.map_items ?? []) add((mapItem as { media_item?: MediaItem }).media_item)
		for (const item of section.section_items ?? []) {
			if (item.media_type) add(item)
			add(item.media_item)
		}
	}

	return ids
}

/**
 * The one size an image is stored at, uncropped: the body stands in for every size offline, and a Fullscreen
 * crop would show a stop's row a portrait slice of a landscape photo.
 */
const offlineImageSize = (devicePixelRatio: number): { width: number; height: number } => {
	const { width, height } = ImageSizePresetMap[ImageSizePreset.Fullscreen]
	const side = Math.max(width, height) * devicePixelRatio

	return { width: side, height: side }
}

/**
 * The file behind one media item. An image is keyed by `normalizeImageKey`, the key the worker falls back to
 * offline, so one body answers every signed size. Video streams and is not downloaded.
 */
export const mediaFileEntries = async (mediaItem: MediaItem, context: ExpandContext): Promise<DownloadEntry[]> => {
	const { id, media_type: mediaType } = mediaItem
	if (id === undefined) return []

	if (mediaType === MediaItem.MediaTypeEnum.Audio) {
		const source = await resolveAudioSource(mediaItem)

		return source ? [{ type: 'media_file', id, urls: [source.src] }] : []
	}

	if (mediaType === MediaItem.MediaTypeEnum.Image) {
		const file = mediaItem.file as FileRec | undefined
		if (file?.id === undefined) return []

		const url = generateImageUrl(file, offlineImageSize(context.devicePixelRatio))

		return [{ type: 'media_file', id, urls: [url], cacheKey: normalizeImageKey(url) }]
	}

	return []
}

/** The files behind media items carried inline, such as a cover: there is no document to fetch first. */
export const inlineMediaEntries = async (
	mediaItems: (MediaItem | undefined)[],
	context: ExpandContext,
): Promise<DownloadEntry[]> => {
	const entries: DownloadEntry[] = []

	for (const mediaItem of mediaItems) {
		if (mediaItem) entries.push(...(await mediaFileEntries(mediaItem, context)))
	}

	return dedupe(entries)
}

/** The thumbnails a stop list draws beside its stops, which the items index carries inline. */
export const itemCoverEntries = (items: CollectionItem[], context: ExpandContext): Promise<DownloadEntry[]> =>
	inlineMediaEntries(
		items.flatMap(({ cover_image_grid: grid, cover_image: cover }) => [grid, cover]),
		context,
	)

/**
 * The tour page's cover and audio preview, and first the `full=true` document a single page tour reads in
 * place of the items index where that is a document of its own: the stop list cannot open offline without it.
 */
export const expandCollectionDocument = async (
	collectionId: number,
	collection: GetCollectionsShowResponse,
	index: ItemIndex,
	context: ExpandContext,
): Promise<DownloadEntry[]> => {
	const media = await inlineMediaEntries([collection.cover_image, collection.audio_preview], context)
	const item = index.collections?.get(collectionId)
	if (!item || collection.initial_layout !== INITIAL_LAYOUT_SINGLE_PAGE) return media

	const { api, accountId, language } = context
	const urls = api.collectionFullUrls(item.accountId ?? accountId, collectionId, item.version, language)

	return dedupe([{ type: 'collection_full', id: collectionId, urls }, ...media])
}

/**
 * The seed of a download, in the order a visitor walks it: the tour itself, then each stop in position
 * order. Media items are not here — a screen names its own, so they join the queue as its document lands.
 */
export const expandCollection = (
	collectionId: number,
	order: CollectionItem[],
	index: ItemIndex,
	context: ExpandContext,
): DownloadEntry[] =>
	dedupe([...collectionEntries(collectionId, index, context), ...expandItems(order, index, context)])

/** One published item index turned into its stops, in the order the index lists them. */
export const expandItems = (order: CollectionItem[], index: ItemIndex, context: ExpandContext): DownloadEntry[] => {
	const entries: DownloadEntry[] = []

	for (const { item_type: itemType, item_id: itemId } of order) {
		const section = itemType ? ITEM_TYPE_SECTIONS[itemType] : undefined
		if (itemId === undefined || !section) continue

		if (section === 'collections') entries.push(...collectionEntries(itemId, index, context))
		else {
			const entry = entryFor(section, itemId, index, context)
			if (entry) entries.push(entry)
		}
	}

	return dedupe(entries)
}

/** The media item entries a screen's document names, so a stop's media follows the stop into the queue. */
export const expandScreenMedia = (
	screen: GetScreensShowResponse,
	index: ItemIndex,
	context: ExpandContext,
): DownloadEntry[] =>
	dedupe(screenMediaItemIds(screen).flatMap(id => entryFor('media_items', id, index, context) ?? []))

/** Everything the manifest lists that walking the tour did not reach — unreferenced quizzes, forms, maps. */
export const expandRemainder = (index: ItemIndex, context: ExpandContext, taken: DownloadEntry[]): DownloadEntry[] => {
	const claimed = new Set(taken.map(({ urls }) => urls[0]))
	const entries: DownloadEntry[] = []

	for (const type of DOWNLOADABLE_TYPES) {
		for (const id of index[type]?.keys() ?? []) {
			const entry = entryFor(type, id, index, context)
			if (entry && !claimed.has(entry.urls[0])) entries.push(entry)
		}
	}

	return dedupe(entries)
}

const dedupe = (entries: DownloadEntry[]): DownloadEntry[] => {
	const seen = new Set<string>()

	return entries.filter(({ urls }) => {
		if (!urls[0] || seen.has(urls[0])) return false
		seen.add(urls[0])
		return true
	})
}
